import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Beaker, Sparkles, Upload, ExternalLink, AlertTriangle, Play, Trash2, FolderOpen,
  CheckCircle2, X, Loader2, Trophy, Pencil,
} from 'lucide-react';
import { api, parseApiError, type MatchCardV2Response, type ShotQualityFlag, type OcrFeedEvent, type OcrProgressEvent } from '../lib/api';
import { cn } from '../lib/cn';
import { SETTLE, MORPH, settleDelay } from '../lib/motion';
import { ShotInspectGrid } from './ShotInspectGrid';
// Consensus aggregation is single-sourced from lib/consensus.ts — this page
// used to carry its own hand-maintained duplicate of normaliseName / dice /
// snapToRosterScored / aggregateConsensus (a real, documented drift risk —
// see MatchCardDemo.test.ts's header comment on the null-guard fix that had
// to land in BOTH copies independently). Importing the shared implementation
// instead keeps the Lab and the live match card byte-for-byte identical on
// name-snapping AND on Lisa's 2026-07-13 K/D-strip-precedence policy
// (applyUploaderStripPrecedence) — exactly the "two layers computing cells
// that can silently disagree" risk that policy call flagged. Re-exported
// below (not just imported) so MatchCardDemo.test.ts's existing `import {
// normaliseName, snapToRosterScored, aggregateConsensus, ... } from
// './MatchCardDemo'` keeps working with zero test-file changes.
import {
  normaliseName, snapToRosterScored, aggregateConsensus, applyUploaderStripPrecedence,
  type DropDiagnostic, type NameVote,
} from '../lib/consensus';
export { normaliseName, snapToRosterScored, aggregateConsensus };

// ─────────────────────────────────────────────────────────────────────────────
// OCR Lab — founder-only end-to-end pipeline test environment.
//
// Workflow:
//   1. Spawn a fresh demo match (BO3 / BO5)
//   2. Drop ANY folder of screenshots — no naming convention required
//   3. Map each file to a (game, clan, player) slot via dropdowns
//      (auto-guesses from filename if recognisable)
//   4. Upload all → each file goes into its assigned slot
//   5. Run OCR pipeline (server-side async via OcrBatchRunner)
//   6. Live-poll the match card; watch slots fill in
//   7. Inspect the consensus scoreboard, per-shot quality flags,
//      cost summary; flag bad shots to exclude and re-run
//
// Tests "what if 1 of 24 is a selfie": the OCR pipeline marks that shot
// OFF_TOPIC, the consensus from the other 23 still produces a clean board.
// ─────────────────────────────────────────────────────────────────────────────

// Loose patterns for auto-suggestion only — manual override always available.
//
// Tier 1: structured convention from the demo fixtures, e.g.
//   PPR_g2-SND_R4G3_K7-D6_20260423-214036.jpg
//   MOM_g1-HP_D_Rose_K32-D27_20260426-095517.png
const STRUCTURED_PATTERN = /^([A-Za-z0-9]+)_g(\d+)-([A-Za-z]+)_(.+?)_K\d+-D\d+/i;
//
// Tier 2: legacy "g1...c1...p2" loose pattern.
const LEGACY_PATTERN = /g(\d+).*?(c1|c2|guild|jsoc|mom|ppr|side1|side2|home|away|red|blue).*?p?(\d+)/i;
//
// Tier 3: clan tag + mode (no game number, infer from mode order).
const TAG_MODE_PATTERN = /^([A-Za-z0-9]+)_([A-Za-z]+)_(.+?)(?:_|\.)/i;

const MODE_TO_GAME: Record<string, number> = {
  hp: 1, hardpoint: 1,
  snd: 2, search: 2,
  ol: 3, overload: 3,
};

// Per-bucket counter to distribute auto-suggested player indices when filename
// has no explicit pX token. Reset each time the user picks a new folder.
type SeqCounter = Map<string, number>;

interface Spawned {
  compId: string;
  matchId: string;
  clan1: string;
  clan2: string;
  clan1Roster: string[];
  clan2Roster: string[];
}

// Reload re-hydration: a page reload mid-run previously lost the spawned
// match entirely (matchId/compId lived only in component state) — the Lab
// reset all the way back to step 1 even though the match was still live
// server-side. Persist just enough to look it back up.
const SPAWN_STORAGE_KEY = 'ggm:ocrLabSpawn';

interface PersistedSpawn extends Spawned {
  format: 'BO3' | 'BO5';
}

/** Pure parse/validate, no storage or network access — safe to unit-test
 *  directly. Returns null for anything missing, malformed, or from an old
 *  schema version rather than handing a half-shaped object to the caller. */
export function parsePersistedSpawn(raw: string | null): PersistedSpawn | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<PersistedSpawn>;
    if (typeof parsed?.matchId !== 'string' || typeof parsed?.compId !== 'string') return null;
    if (!Array.isArray(parsed.clan1Roster) || !Array.isArray(parsed.clan2Roster)) return null;
    return parsed as PersistedSpawn;
  } catch {
    return null;
  }
}

interface FileEntry {
  file: File;
  /** Stable id per render. */
  id: string;
  gameNum: number;
  clan: 'c1' | 'c2';
  playerIndex: number;     // 1..teamSize
  /** Set after upload. */
  uploaded?: boolean;
  uploadError?: string;
}

export function inferClan(tag: string, clan1Name: string, clan2Name: string): 'c1' | 'c2' {
  // Defensive: these are file-drop-derived strings, but null-safety here
  // costs nothing and matches the same guard pattern applied everywhere else
  // a name gets lowercased in this file.
  const t = (tag ?? '').toLowerCase();
  if ((clan1Name ?? '').toLowerCase().includes(t)) return 'c1';
  if ((clan2Name ?? '').toLowerCase().includes(t)) return 'c2';
  if (/^(mom|guild|home|side1|red|c1)/i.test(t)) return 'c1';
  return 'c2';
}

function autoSuggest(
  name: string,
  gameCount: number,
  teamSize: number,
  clan1Name: string,
  clan2Name: string,
  seq: SeqCounter,
): { gameNum: number; clan: 'c1' | 'c2'; playerIndex: number } {
  const base = name.split('/').pop() ?? name;

  // Tier 1: structured fixture pattern — full info, sequential player slot.
  const struct = base.match(STRUCTURED_PATTERN);
  if (struct) {
    const clan = inferClan(struct[1], clan1Name, clan2Name);
    const gameNum = Math.min(Math.max(parseInt(struct[2], 10), 1), gameCount);
    const bk = `${clan}-${gameNum}`;
    const next = (seq.get(bk) ?? 0) + 1;
    seq.set(bk, next);
    return { gameNum, clan, playerIndex: Math.min(Math.max(next, 1), teamSize) };
  }

  // Tier 2: legacy "g1...c1...p2"
  const legacy = base.match(LEGACY_PATTERN);
  if (legacy) {
    const gameNum = Math.min(Math.max(parseInt(legacy[1], 10), 1), gameCount);
    const clanRaw = legacy[2].toLowerCase();
    const clan: 'c1' | 'c2' = clanRaw.match(/^(c1|guild|mom|side1|home|red)$/) ? 'c1' : 'c2';
    const playerIndex = Math.min(Math.max(parseInt(legacy[3], 10), 1), teamSize);
    return { gameNum, clan, playerIndex };
  }

  // Tier 3: clan + mode (no game num) — infer from mode order, sequential player.
  const tagMode = base.match(TAG_MODE_PATTERN);
  if (tagMode) {
    const clan = inferClan(tagMode[1], clan1Name, clan2Name);
    const modeNorm = tagMode[2].toLowerCase();
    const gameNum = Math.min(Math.max(MODE_TO_GAME[modeNorm] ?? 1, 1), gameCount);
    const bk = `${clan}-${gameNum}`;
    const next = (seq.get(bk) ?? 0) + 1;
    seq.set(bk, next);
    return { gameNum, clan, playerIndex: Math.min(Math.max(next, 1), teamSize) };
  }

  return { gameNum: 1, clan: 'c1', playerIndex: 1 };
}

// ─────────────────────────────────────────────────────────────────────────────

export function MatchCardDemo() {
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.me, retry: false });
  if (!me) return <Shell><p className="font-mono text-sm text-iron-400">Loading…</p></Shell>;
  if (me.role !== 'founder') return <Shell><p className="font-mono text-sm text-red-300">Founders only.</p></Shell>;
  return (
    <Shell>
      <header className={cn('mb-6 flex items-start gap-4', SETTLE)} style={settleDelay(0)}>
        <div className="rounded-xl bg-white/[0.05] p-3">
          <Beaker size={28} className="text-ink-secondary" />
        </div>
        <div className="flex-1">
          <h1 className="font-display text-2xl font-extrabold uppercase tracking-wider text-white">OCR Lab — Batch v2</h1>
          <p className="mt-1 max-w-2xl font-mono text-sm text-iron-400">
            End-to-end test rig for the v2 OCR pipeline. Spawn a fake match, drop any folder of screenshots, map them to slots, run the pipeline, watch the consensus scoreboard build live.
          </p>
        </div>
      </header>
      <MatchBatchPipelineInner />
    </Shell>
  );
}

// Reusable inner component (no Shell, no header, no role gate) so the
// OCR Lab tabbed page can embed it alongside the legacy single-shot test.
export function MatchBatchPipelineInner() {
  const queryClient = useQueryClient();
  const [spawned, setSpawned] = useState<Spawned | null>(null);
  const [format, setFormat] = useState<'BO3' | 'BO5'>('BO3');
  const [files, setFiles] = useState<FileEntry[]>([]);
  const [log, setLog] = useState<string[]>([]);
  const [batchInFlight, setBatchInFlight] = useState(false);

  const gameCount = format === 'BO3' ? 3 : 5;
  const teamSize = 4;

  // Reload re-hydration — on mount, if a spawn was persisted and the match
  // still exists server-side (GET /matchcard/:id), restore it instead of
  // resetting the Lab to step 1. Runs once; `spawned` starting non-null would
  // mean this already happened in the same mount.
  useEffect(() => {
    if (spawned) return;
    const saved = parsePersistedSpawn(sessionStorage.getItem(SPAWN_STORAGE_KEY));
    if (!saved) return;
    api.matchCardGet(saved.matchId, saved.compId)
      .then(() => {
        setSpawned({
          compId: saved.compId, matchId: saved.matchId,
          clan1: saved.clan1, clan2: saved.clan2,
          clan1Roster: saved.clan1Roster, clan2Roster: saved.clan2Roster,
        });
        setFormat(saved.format);
        setLog([`✓ Restored ${saved.matchId} after reload`]);
      })
      // Match no longer exists (cleaned up, expired) — drop the stale entry
      // so future reloads don't keep retrying a dead match.
      .catch(() => sessionStorage.removeItem(SPAWN_STORAGE_KEY));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live poll the match card once spawned. Tightens to 1.5s while a batch is
  // in-flight so the slot phase chips feel snappy.
  const cardQuery = useQuery({
    queryKey: ['matchcard', spawned?.matchId, spawned?.compId],
    queryFn: () => api.matchCardGet(spawned!.matchId, spawned!.compId),
    enabled: !!spawned,
    refetchInterval: spawned ? (batchInFlight ? 1500 : 3000) : false,
  });

  // Live activity feed — polls only while a batch is in flight, fetches only
  // events newer than the last sk we've seen.
  const [feedSince, setFeedSince] = useState<string | undefined>(undefined);
  const [feedEvents, setFeedEvents] = useState<OcrFeedEvent[]>([]);
  const feedQuery = useQuery({
    queryKey: ['ocrfeed', spawned?.matchId, feedSince],
    queryFn: () => api.matchCardOcrFeed(spawned!.matchId, feedSince),
    enabled: !!spawned && batchInFlight,
    refetchInterval: spawned && batchInFlight ? 1500 : false,
  });
  useEffect(() => {
    if (!feedQuery.data?.events?.length) return;
    setFeedEvents(prev => [...prev, ...feedQuery.data!.events].slice(-200));
    if (feedQuery.data.latestSk) setFeedSince(feedQuery.data.latestSk);
  }, [feedQuery.data]);
  // Reset feed when a new batch starts
  useEffect(() => {
    if (batchInFlight) {
      setFeedEvents([]);
      setFeedSince(undefined);
    }
  }, [batchInFlight]);

  const spawnMutation = useMutation({
    mutationFn: () => api.demoSpawnMatchCard({ format }),
    onSuccess: (res) => {
      const next: Spawned = {
        compId: res.compId, matchId: res.matchId,
        clan1: res.clan1, clan2: res.clan2,
        clan1Roster: res.clan1Roster, clan2Roster: res.clan2Roster,
      };
      setSpawned(next);
      sessionStorage.setItem(SPAWN_STORAGE_KEY, JSON.stringify({ ...next, format } satisfies PersistedSpawn));
      setFiles([]);
      setLog([`✓ Spawned ${res.matchId}`]);
    },
  });

  const runOcrMutation = useMutation({
    mutationFn: (excludeSlotSks: string[]) => api.runOcrBatch(spawned!.matchId, spawned!.compId, excludeSlotSks),
    onMutate: () => setBatchInFlight(true),
    onSuccess: (res) => {
      setLog(prev => [...prev, `→ OCR batch dispatched (${res.shotsQueued ?? '?'} shots queued). Polling for results...`]);
    },
    onError: (err) => {
      setBatchInFlight(false);
      setLog(prev => [...prev, `✗ OCR dispatch failed: ${(err as Error).message}`]);
    },
  });

  const flagMutation = useMutation({
    mutationFn: (vars: { gameNum: number; playerId: string; remove: boolean }) =>
      api.shotFlag(spawned!.matchId, vars.gameNum, vars.playerId, spawned!.compId, 'ADMIN_FLAGGED' as ShotQualityFlag, { remove: vars.remove }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['matchcard', spawned!.matchId, spawned!.compId] }),
  });

  // One-click demo: server copies fixture screenshots into slots, then we kick OCR.
  const loadDemoMutation = useMutation({
    mutationFn: () => api.loadDemoFixture(spawned!.matchId, spawned!.compId),
    onSuccess: (res) => {
      setLog(prev => [...prev, `✓ Demo fixture loaded: ${res.copied}/${res.totalFixtures} slots populated${res.unmatched ? ` (${res.unmatched} unmatched)` : ''}`]);
      queryClient.invalidateQueries({ queryKey: ['matchcard', spawned!.matchId, spawned!.compId] });
      // Auto-trigger OCR
      setTimeout(() => runOcrMutation.mutate([]), 800);
    },
    onError: (err) => setLog(prev => [...prev, `✗ Demo fixture load failed: ${(err as Error).message}`]),
  });

  // Watch for batch completion
  useEffect(() => {
    if (!batchInFlight || !cardQuery.data) return;
    const allDone = cardQuery.data.slots.every(s => s.status === 'OCR_DONE' || s.status === 'FLAGGED' || s.status === 'PENDING');
    const anyProcessing = cardQuery.data.slots.some(s => s.status === 'OCR_PROCESSING' || s.status === 'UPLOADED');
    if (allDone && !anyProcessing) {
      setBatchInFlight(false);
      const summary = (cardQuery.data.match as { ocrBatchSummary?: { elapsedMs: number; cost: { estimatedUsd: number } } }).ocrBatchSummary;
      if (summary) {
        setLog(prev => [...prev, `✓ Batch complete in ${(summary.elapsedMs / 1000).toFixed(1)}s — cost ~$${summary.cost.estimatedUsd.toFixed(4)}`]);
      } else {
        setLog(prev => [...prev, `✓ Batch complete`]);
      }
    }
  }, [batchInFlight, cardQuery.data]);

  // W11: MANUAL routing (no parent org / org tokens depleted) never touches
  // a single SLOT row — every slot stays parked at UPLOADED, so the
  // slot-status watcher above never sees `anyProcessing` clear and the
  // spinner never resolves. The runner now emits a MANUAL_ROUTED feed event
  // specifically so this terminal state has a signal to key off.
  useEffect(() => {
    if (!batchInFlight) return;
    const manualEvent = feedEvents.find(e => e.event.kind === 'MANUAL_ROUTED');
    if (!manualEvent) return;
    setBatchInFlight(false);
    setLog(prev => [...prev, `⚠ Routed to MANUAL entry — ${(manualEvent.event as { message: string }).message}`]);
  }, [batchInFlight, feedEvents]);

  return (
    <>
      {!spawned && (
        <div className={SETTLE} style={settleDelay(0)}>
          <SpawnPanel format={format} setFormat={setFormat} mutation={spawnMutation} />
        </div>
      )}

      {spawned && (
        <div className="space-y-6">
          <div className={SETTLE} style={settleDelay(0)}>
            <SpawnedBanner spawned={spawned} format={format} onReset={() => { setSpawned(null); setFiles([]); sessionStorage.removeItem(SPAWN_STORAGE_KEY); }} />
          </div>

          <div className={SETTLE} style={settleDelay(1)}>
            <UploadPanel
              spawned={spawned}
              files={files}
              setFiles={setFiles}
              gameCount={gameCount}
              teamSize={teamSize}
              log={(line) => setLog(prev => [...prev, line])}
              cardQuery={cardQuery}
              loadDemo={loadDemoMutation}
            />
          </div>

          <div className={SETTLE} style={settleDelay(2)}>
            <RunPanel
              spawned={spawned}
              cardData={cardQuery.data}
              batchInFlight={batchInFlight}
              onRun={() => {
                const excluded = (cardQuery.data?.slots ?? [])
                  .filter(s => (s as unknown as { excludedFromConsensus?: boolean }).excludedFromConsensus)
                  .map(s => s.sk);
                runOcrMutation.mutate(excluded);
              }}
            />
          </div>

          {cardQuery.data && (
            <div className={SETTLE} style={settleDelay(3)}>
              <ShotInspectGrid
                data={cardQuery.data}
                spawned={spawned}
                events={feedEvents}
                onFlag={(slot) => {
                  const excluded = (slot as unknown as { excludedFromConsensus?: boolean }).excludedFromConsensus ?? false;
                  flagMutation.mutate({ gameNum: slot.gameNum, playerId: slot.playerId, remove: excluded });
                }}
              />
            </div>
          )}

          {cardQuery.data && (
            <div className={SETTLE} style={settleDelay(4)}>
              <ConsensusScoreboard data={cardQuery.data} spawned={spawned} />
            </div>
          )}

          <div className={SETTLE} style={settleDelay(5)}>
            <ActivityFeedPanel events={feedEvents} batchInFlight={batchInFlight} clientLog={log} />
          </div>
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function Shell({ children }: { children: React.ReactNode }) {
  return <div className="mx-auto max-w-6xl px-4 py-6">{children}</div>;
}

function SpawnPanel({ format, setFormat, mutation }: {
  format: 'BO3' | 'BO5';
  setFormat: (f: 'BO3' | 'BO5') => void;
  mutation: { mutate: () => void; isPending: boolean; error: unknown };
}) {
  return (
    <section className="rounded-xl bg-paper-elevated p-6">
      <h2 className="mb-3 font-display text-lg font-bold uppercase tracking-wider text-iron-200">1 · Spawn a fresh demo match</h2>
      <p className="mb-4 font-mono text-xs text-iron-400">
        Creates 2 synthetic clans with 4 ghost players each, a fake comp, Match Card v2 in LIVE state. Tagged <code className="rounded bg-white/[0.06] px-1 text-ink-secondary">isDemo: true</code> for easy cleanup.
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <label className="font-mono text-xs text-iron-400">Format:</label>
        <select
          value={format}
          onChange={e => setFormat(e.target.value as 'BO3' | 'BO5')}
          className="input px-2 py-1 font-mono text-xs"
        >
          <option value="BO3">BO3 — 24 slots</option>
          <option value="BO5">BO5 — 40 slots</option>
        </select>
        <button
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          className={cn('inline-flex items-center gap-2 rounded-md bg-accent-500 px-4 py-2 font-display text-sm font-bold uppercase tracking-wider text-[#0a0a0c] hover:bg-accent-400 disabled:opacity-50', MORPH)}
        >
          <Sparkles size={14} /> {mutation.isPending ? 'Spawning…' : 'Spawn'}
        </button>
        {mutation.error ? <span className="font-mono text-xs text-red-400">{(mutation.error as Error).message}</span> : null}
      </div>
    </section>
  );
}

function SpawnedBanner({ spawned, format, onReset }: { spawned: Spawned; format: string; onReset: () => void }) {
  const cardUrl = `/matchcard/${encodeURIComponent(spawned.matchId)}?compId=${encodeURIComponent(spawned.compId)}`;
  return (
    <section className="flex items-center justify-between gap-3 rounded-xl bg-paper-elevated px-4 py-3">
      <div className="font-mono text-xs text-iron-300">
        <span className="text-emerald-300">match spawned</span> · {format} ·
        <span className="ml-1 text-iron-100">{spawned.clan1}</span>
        <span className="mx-1 text-iron-500">vs</span>
        <span className="text-iron-100">{spawned.clan2}</span>
        <span className="ml-2 text-iron-500">{spawned.matchId}</span>
      </div>
      <div className="flex items-center gap-2">
        <Link to={cardUrl} target="_blank" className={cn('inline-flex items-center gap-1 rounded-md bg-white/[0.06] px-3 py-1 font-mono text-xs text-iron-200 hover:bg-white/[0.1]', MORPH)}>
          Live card <ExternalLink size={11} />
        </Link>
        <button onClick={onReset} className={cn('inline-flex items-center gap-1 rounded-md bg-red-500/15 px-3 py-1 font-mono text-xs text-red-300 hover:bg-red-500/25', MORPH)}>
          <X size={11} /> Reset
        </button>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Upload panel — folder picker, manual slot assignment grid, upload-all
// ─────────────────────────────────────────────────────────────────────────────

function UploadPanel({ spawned, files, setFiles, gameCount, teamSize, log, cardQuery, loadDemo }: {
  spawned: Spawned;
  files: FileEntry[];
  setFiles: React.Dispatch<React.SetStateAction<FileEntry[]>>;
  gameCount: number;
  teamSize: number;
  log: (line: string) => void;
  cardQuery: { data?: MatchCardV2Response };
  loadDemo: { mutate: () => void; isPending: boolean };
}) {
  const [busy, setBusy] = useState(false);

  const handlePicked = (picked: FileList | null) => {
    if (!picked) return;
    const list: FileEntry[] = [];
    const seq: SeqCounter = new Map();
    // Sort files alphabetically so structured names (g1, g2, g3) get player 1..N in order
    const files = Array.from(picked)
      .filter(f => f.type.startsWith('image/'))
      .sort((a, b) => (a.webkitRelativePath || a.name).localeCompare(b.webkitRelativePath || b.name));
    let i = 0;
    for (const file of files) {
      const path = file.webkitRelativePath || file.name;
      const guess = autoSuggest(path, gameCount, teamSize, spawned.clan1, spawned.clan2, seq);
      list.push({
        file, id: `${Date.now()}-${i++}-${file.name}`,
        gameNum: guess.gameNum, clan: guess.clan, playerIndex: guess.playerIndex,
      });
    }
    setFiles(list);
    log(`Picked ${list.length} files (auto-mapped where filenames followed the convention)`);
  };

  const updateAssignment = (id: string, patch: Partial<Pick<FileEntry, 'gameNum' | 'clan' | 'playerIndex'>>) => {
    setFiles(prev => prev.map(f => f.id === id ? { ...f, ...patch } : f));
  };

  const removeFile = (id: string) => setFiles(prev => prev.filter(f => f.id !== id));

  // Conflict detection: two files mapped to the same (gameNum, clan, playerIdx)
  const conflicts = useMemo(() => {
    const seen = new Map<string, number>();
    const conflictSet = new Set<string>();
    for (const f of files) {
      const key = `${f.gameNum}-${f.clan}-${f.playerIndex}`;
      const count = (seen.get(key) ?? 0) + 1;
      seen.set(key, count);
      if (count > 1) conflictSet.add(key);
    }
    return conflictSet;
  }, [files]);

  // Slot grid emptiness — what slots are NOT covered by any uploaded file
  const uploadedCount = cardQuery.data?.slots.filter(s => s.s3Key).length ?? 0;
  const totalSlots = cardQuery.data?.summary.slotsExpected ?? 0;

  const uploadAll = async () => {
    setBusy(true);
    try {
      const queue = [...files.filter(f => !f.uploaded)];
      const upload = async (f: FileEntry) => {
        try {
          const ct = f.file.type as 'image/png' | 'image/jpeg' | 'image/webp';
          const playerId = f.clan === 'c1' ? spawned.clan1Roster[f.playerIndex - 1] : spawned.clan2Roster[f.playerIndex - 1];
          if (!playerId) throw new Error(`No player at clan=${f.clan} index=${f.playerIndex}`);
          const { url, s3Key } = await api.slotUploadUrl(spawned.matchId, f.gameNum, playerId, spawned.compId, ct);
          const putRes = await fetch(url, { method: 'PUT', body: f.file, headers: { 'Content-Type': ct } });
          if (!putRes.ok) throw new Error(`PUT ${putRes.status}`);
          await api.slotConfirm(spawned.matchId, f.gameNum, playerId, spawned.compId, s3Key);
          setFiles(prev => prev.map(x => x.id === f.id ? { ...x, uploaded: true, uploadError: undefined } : x));
          log(`✓ ${f.file.name} → G${f.gameNum} ${f.clan}-p${f.playerIndex}`);
        } catch (err) {
          const msg = (err as Error).message;
          setFiles(prev => prev.map(x => x.id === f.id ? { ...x, uploadError: msg } : x));
          log(`✗ ${f.file.name}: ${msg}`);
        }
      };
      const workers = 4;
      await Promise.all(Array.from({ length: workers }, async () => {
        while (queue.length > 0) {
          const f = queue.shift()!;
          await upload(f);
        }
      }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-xl bg-paper-elevated p-6">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-display text-lg font-bold uppercase tracking-wider text-iron-200">2 · Drop screenshots</h2>
        <span className="font-mono text-xs text-iron-500">{uploadedCount}/{totalSlots} slots populated server-side</span>
      </div>
      <p className="mb-4 font-mono text-xs text-iron-400">
        Pick a folder or any selection of images, or fire the canned demo set. Filenames following the convention <code className="rounded bg-white/[0.06] px-1 text-emerald-300">{`{clan}_g{N}-{mode}_{player}_K{k}-D{d}_{ts}.ext`}</code> auto-populate the slot grid. Bad/wrong/selfie images are fine: drop them in and watch the OCR pipeline flag them as <code className="rounded bg-white/[0.06] px-1 text-amber-300">OFF_TOPIC</code> or <code className="rounded bg-white/[0.06] px-1 text-red-300">STAGE2_REJECTED</code> when you run the batch.
      </p>

      <div className="mb-3 rounded-md bg-white/[0.04] p-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="font-display text-xs font-bold uppercase tracking-wider text-iron-200">One-click demo</p>
            <p className="mt-0.5 font-mono text-[11px] text-iron-400">
              Copies the canned MOM_ANARCHY vs PPR fixture (24 real screenshots) into this match's slots and fires the OCR pipeline. Watch the activity feed do its thing.
            </p>
          </div>
          <button
            onClick={() => loadDemo.mutate()}
            disabled={loadDemo.isPending}
            className={cn('inline-flex shrink-0 items-center gap-2 rounded-md bg-white/[0.07] px-4 py-2 font-display text-xs font-bold uppercase tracking-wider text-ink-primary hover:bg-white/[0.1] disabled:opacity-50', MORPH)}
          >
            <Sparkles size={14} /> {loadDemo.isPending ? 'Loading…' : 'Run canned demo'}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <label className={cn('inline-flex cursor-pointer items-center gap-2 rounded-md bg-white/[0.06] px-4 py-2 font-display text-xs font-bold uppercase tracking-wider text-iron-200 hover:bg-white/[0.1]', MORPH)}>
          <FolderOpen size={14} /> Pick folder
          <input
            type="file"
            // @ts-expect-error — webkitdirectory is non-standard but works on Chromium/Firefox/Safari
            webkitdirectory=""
            directory=""
            multiple
            className="hidden"
            onChange={e => handlePicked(e.target.files)}
          />
        </label>
        <label className={cn('inline-flex cursor-pointer items-center gap-2 rounded-md bg-white/[0.06] px-4 py-2 font-display text-xs font-bold uppercase tracking-wider text-iron-200 hover:bg-white/[0.1]', MORPH)}>
          <Upload size={14} /> Pick files
          <input type="file" multiple accept="image/*" className="hidden" onChange={e => handlePicked(e.target.files)} />
        </label>
        {files.length > 0 && (
          <>
            <button
              onClick={uploadAll}
              disabled={busy || conflicts.size > 0}
              className={cn('inline-flex items-center gap-2 rounded-md bg-accent-500 px-4 py-2 font-display text-xs font-bold uppercase tracking-wider text-[#0a0a0c] hover:bg-accent-400 disabled:opacity-50', MORPH)}
            >
              <Upload size={14} /> {busy ? `Uploading…` : `Upload all ${files.filter(f => !f.uploaded).length}`}
            </button>
            <button
              onClick={() => setFiles([])}
              disabled={busy}
              className={cn('inline-flex items-center gap-2 rounded-md bg-red-500/15 px-3 py-2 font-mono text-xs text-red-300 hover:bg-red-500/25 disabled:opacity-50', MORPH)}
            >
              <Trash2 size={12} /> Clear
            </button>
          </>
        )}
      </div>

      {conflicts.size > 0 && (
        <div className="mt-3 rounded-md bg-red-500/15 px-3 py-2 font-mono text-xs text-red-200">
          <AlertTriangle size={12} className="mr-1 inline" /> {conflicts.size} slot conflict{conflicts.size === 1 ? '' : 's'} — two or more files target the same slot. Resolve before uploading.
        </div>
      )}

      {files.length > 0 && (
        <div className="mt-4 max-h-[420px] overflow-y-auto rounded-md bg-paper-inset">
          <table className="min-w-full text-left font-mono text-[11px]">
            <thead className="sticky top-0 bg-paper-panel text-iron-400">
              <tr>
                <th className="px-2 py-1.5">#</th>
                <th className="px-2 py-1.5">file</th>
                <th className="px-2 py-1.5">game</th>
                <th className="px-2 py-1.5">clan</th>
                <th className="px-2 py-1.5">player</th>
                <th className="px-2 py-1.5">status</th>
                <th className="px-2 py-1.5"></th>
              </tr>
            </thead>
            <tbody>
              {files.map((f, idx) => {
                const conflictKey = `${f.gameNum}-${f.clan}-${f.playerIndex}`;
                const hasConflict = conflicts.has(conflictKey);
                return (
                  <tr key={f.id} className={cn(idx > 0 && 'border-t border-white/[0.04]', hasConflict && 'bg-red-500/15')}>
                    <td className="px-2 py-1 text-iron-500">{idx + 1}</td>
                    <td className="px-2 py-1 text-iron-100">{f.file.name}</td>
                    <td className="px-2 py-1">
                      <select
                        value={f.gameNum}
                        onChange={e => updateAssignment(f.id, { gameNum: parseInt(e.target.value, 10) })}
                        disabled={f.uploaded}
                        className="input px-1 py-0.5 text-[11px] disabled:opacity-50"
                      >
                        {Array.from({ length: gameCount }, (_, i) => i + 1).map(g => <option key={g} value={g}>G{g}</option>)}
                      </select>
                    </td>
                    <td className="px-2 py-1">
                      <select
                        value={f.clan}
                        onChange={e => updateAssignment(f.id, { clan: e.target.value as 'c1' | 'c2' })}
                        disabled={f.uploaded}
                        className="input px-1 py-0.5 text-[11px] disabled:opacity-50"
                      >
                        <option value="c1">{spawned.clan1}</option>
                        <option value="c2">{spawned.clan2}</option>
                      </select>
                    </td>
                    <td className="px-2 py-1">
                      <select
                        value={f.playerIndex}
                        onChange={e => updateAssignment(f.id, { playerIndex: parseInt(e.target.value, 10) })}
                        disabled={f.uploaded}
                        className="input px-1 py-0.5 text-[11px] disabled:opacity-50"
                      >
                        {Array.from({ length: teamSize }, (_, i) => i + 1).map(p => <option key={p} value={p}>p{p}</option>)}
                      </select>
                    </td>
                    <td className="px-2 py-1">
                      {f.uploaded ? <span className="text-emerald-300"><CheckCircle2 size={11} className="mr-1 inline" />uploaded</span>
                        : f.uploadError ? <span className="text-red-300">{f.uploadError}</span>
                        : <span className="text-iron-500">pending</span>}
                    </td>
                    <td className="px-2 py-1">
                      {!f.uploaded && (
                        <button onClick={() => removeFile(f.id)} className="text-iron-500 hover:text-red-400">
                          <X size={12} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Run panel — kicks the OCR batch
// ─────────────────────────────────────────────────────────────────────────────

function RunPanel({ spawned: _, cardData, batchInFlight, onRun }: {
  spawned: Spawned;
  cardData: MatchCardV2Response | undefined;
  batchInFlight: boolean;
  onRun: () => void;
}) {
  const uploaded = cardData?.slots.filter(s => s.s3Key).length ?? 0;
  const ocrDone = cardData?.slots.filter(s => s.status === 'OCR_DONE' || s.status === 'FLAGGED').length ?? 0;
  const processing = cardData?.slots.filter(s => s.status === 'OCR_PROCESSING').length ?? 0;

  return (
    <section className="rounded-xl bg-paper-elevated p-6">
      <h2 className="mb-3 font-display text-lg font-bold uppercase tracking-wider text-iron-200">3 · Run the OCR pipeline</h2>
      <p className="mb-4 font-mono text-xs text-iron-400">
        Server-side runs the v2 pipeline on every uploaded slot: enhance → quality score → Stage 1 Nova → name snap → quality-weighted consensus → selective Stage 2. Async (returns immediately, polls for results).
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={onRun}
          disabled={uploaded === 0 || batchInFlight}
          className={cn('inline-flex items-center gap-2 rounded-md bg-accent-500 px-4 py-2 font-display text-sm font-bold uppercase tracking-wider text-[#0a0a0c] hover:bg-accent-400 disabled:opacity-50', MORPH)}
        >
          {batchInFlight ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
          {batchInFlight ? 'Running…' : `Run OCR (${uploaded} shots)`}
        </button>
        <span className="font-mono text-xs text-iron-400">
          {ocrDone} done · {processing} in-flight · {uploaded - ocrDone - processing} pending
        </span>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Consensus scoreboard — derives client-side from slots[].parsedScoreboard
// (reuses logic identical to MatchCardLive ScoreboardConsensus, simplified)
// ─────────────────────────────────────────────────────────────────────────────

// normaliseName / snapToRosterScored / aggregateConsensus / DropDiagnostic /
// NameVote used to be hand-duplicated here — see the import + re-export
// block near the top of this file. Everything below reuses that single
// shared implementation.

const FIELD_LABEL: Record<string, string> = {
  score: 'Score', objScore: 'Obj', kills: 'K', deaths: 'D', time: 'Time',
  plants: 'Plants', defuses: 'Defuses', overloads: 'OL', captures: 'Captures',
};

// Columns shown in the team-board table (mirrors the in-game scoreboard).
// K/D + extras like captures/defuses live in the click-to-expand detail panel
// instead, since they aren't part of the in-game team rows.
const MAIN_COLUMNS = ['score', 'objScore', 'time', 'plants', 'defuses', 'overloads', 'captures'];

// CoD game-mode short codes (as stored on slots / match.gameModes) to readable
// labels for the per-game header chip.
const GAME_MODE_LABEL: Record<string, string> = {
  hp: 'Hardpoint', hardpoint: 'Hardpoint',
  snd: 'Search & Destroy', search: 'Search & Destroy', searchanddestroy: 'Search & Destroy',
  ol: 'Overload', overload: 'Overload',
  ctl: 'Control', control: 'Control',
  dom: 'Domination', domination: 'Domination',
  tdm: 'Team Deathmatch',
};
function gameModeLabel(mode: string | undefined): string {
  if (!mode) return '';
  const key = mode.toLowerCase().replace(/[^a-z]/g, '');
  return GAME_MODE_LABEL[key] ?? mode.toUpperCase();
}

function ConsensusScoreboard({ data, spawned }: { data: MatchCardV2Response; spawned: Spawned }) {
  const queryClient = useQueryClient();
  const rosterTeams = useMemo(() => {
    const m = data.match as { clan1Display?: string[]; clan2Display?: string[]; clan1RosterSnapshot?: string[]; clan2RosterSnapshot?: string[] };
    // Prefer Display (real gamertags) over RosterSnapshot (slot-identity
    // Discord IDs) for snapping OCR'd names + labelling consensus rows.
    const clan1 = m.clan1Display ?? m.clan1RosterSnapshot ?? spawned.clan1Roster ?? [];
    const clan2 = m.clan2Display ?? m.clan2RosterSnapshot ?? spawned.clan2Roster ?? [];
    // Snapshot arrays carry the uploader identity (Discord IDs in prod,
    // synthetic Pdemo-c1-…-pN strings in the canned demo). Same length and
    // order as Display, so index N in either resolves to the same player.
    const clan1Snap = m.clan1RosterSnapshot ?? [];
    const clan2Snap = m.clan2RosterSnapshot ?? [];
    // Map slot.playerId → snapped Display name. Used to attribute uploaderStats
    // by uploader identity rather than OCR'd gamertag — robust against the
    // bottom-strip OCR mangling its own user (e.g. G17 → "617" misread).
    const playerIdToDisplay = new Map<string, string>();
    for (let i = 0; i < clan1Snap.length; i++) {
      const display = clan1[i] ?? clan1Snap[i];
      if (clan1Snap[i] && display) playerIdToDisplay.set(clan1Snap[i], display);
    }
    for (let i = 0; i < clan2Snap.length; i++) {
      const display = clan2[i] ?? clan2Snap[i];
      if (clan2Snap[i] && display) playerIdToDisplay.set(clan2Snap[i], display);
    }
    return { clan1, clan2, playerIdToDisplay };
  }, [data.match, spawned.clan1Roster, spawned.clan2Roster]);
  const roster = useMemo(() => [...rosterTeams.clan1, ...rosterTeams.clan2], [rosterTeams]);

  // Live admin-curated KNOWLEDGE#NAME_SNAP rules. Applied client-side so
  // corrections take effect without re-running OCR.
  const rulesQuery = useQuery({
    queryKey: ['knowledge', 'name-snap', spawned.compId],
    queryFn: () => api.knowledgeListNameSnaps(spawned.compId),
    enabled: !!spawned.compId,
  });
  const manualRules = useMemo(() => {
    const out: Record<string, string> = {};
    for (const rule of rulesQuery.data?.rules ?? []) {
      out[rule.fromKey] = rule.toName;
    }
    return out;
  }, [rulesQuery.data]);

  const { perGame: rawPerGame, players: rawPlayers, extractedNames, drops } = useMemo(
    () => aggregateConsensus(data.slots, roster, manualRules, rosterTeams.playerIdToDisplay),
    [data.slots, roster, manualRules, rosterTeams.playerIdToDisplay],
  );
  // K/D strip precedence (Lisa, 2026-07-13) — same policy, same shared
  // function, as MatchCardLive's ScoreboardConsensus (lib/consensus.ts's own
  // doc comment has the full precedence ladder). The Lab has no server-merge
  // step (it always shows the live client recompute, by design — see this
  // file's header), so this runs directly on aggregateConsensus's own output.
  const { perGame, players } = useMemo(
    () => applyUploaderStripPrecedence(rawPerGame, rawPlayers, data.slots, roster, manualRules, rosterTeams.playerIdToDisplay),
    [rawPerGame, rawPlayers, data.slots, roster, manualRules, rosterTeams.playerIdToDisplay],
  );
  const games = Object.keys(perGame).map(Number).sort();
  const totalDrops = Object.values(drops).reduce((a, b) => a + b.length, 0);

  const [correction, setCorrection] = useState<{ raw: string; suggested: string | null } | null>(null);

  if (games.length === 0) {
    return (
      <section className="rounded-xl bg-paper-elevated p-6">
        <h2 className="mb-2 flex items-center gap-2 font-display text-lg font-bold uppercase tracking-wider text-iron-200">
          <Trophy size={16} /> 5 · Consensus scoreboard
        </h2>
        <p className="font-mono text-xs text-iron-500">No OCR results yet. Run the pipeline above.</p>
      </section>
    );
  }

  const allDrops: DropDiagnostic[] = games.flatMap(g => drops[g] ?? []);

  return (
    <section className="rounded-xl bg-paper-elevated p-6">
      <h2 className="mb-3 flex items-center gap-2 font-display text-lg font-bold uppercase tracking-wider text-iron-200">
        <Trophy size={16} /> 5 · Consensus scoreboard
      </h2>
      <p className="mb-4 font-mono text-xs text-iron-400">
        Player labels show the gamertag the OCR extracted (most-voted across shots). Cell colour shows agreement: emerald = 100%, white = ≥67%, amber = ≥50%, red = &lt;50%.
        {(rulesQuery.data?.rules?.length ?? 0) > 0 && (
          <span className="ml-1 text-emerald-500">· {rulesQuery.data!.rules.length} admin rule{rulesQuery.data!.rules.length === 1 ? '' : 's'} applied</span>
        )}
        {totalDrops > 0 && (
          <span className="ml-1 text-iron-500">· {totalDrops} OCR row{totalDrops === 1 ? '' : 's'} dropped (see diagnostics)</span>
        )}
      </p>

      {games.map(g => {
        const cells = perGame[g];
        const playersInG = players[g] ?? [];
        const fields = [...new Set(cells.map(c => c.field))];
        const fieldOrder = MAIN_COLUMNS.filter(f => fields.includes(f));

        // Split players into the two teams using their roster array of origin
        // (clan1 first half of `roster`, clan2 second half — already sorted by
        // roster index inside aggregateConsensus).
        const clan1Set = new Set(rosterTeams.clan1);
        const clan2Set = new Set(rosterTeams.clan2);
        const clan1Players = playersInG.filter(p => clan1Set.has(p));
        const clan2Players = playersInG.filter(p => clan2Set.has(p));
        // Anyone snapped to a name not in either roster (shouldn't happen, but
        // defensive) sticks with clan1 so we don't silently drop them.
        const orphans = playersInG.filter(p => !clan1Set.has(p) && !clan2Set.has(p));
        const team1Players = orphans.length ? [...clan1Players, ...orphans] : clan1Players;

        const teamTotal = (ps: string[]) =>
          ps.reduce((sum, p) => sum + (cells.find(c => c.player === p && c.field === 'score')?.consensus ?? 0), 0);
        const t1Total = teamTotal(team1Players);
        const t2Total = teamTotal(clan2Players);

        const board1 = { name: spawned.clan1, players: team1Players, total: t1Total };
        const board2 = { name: spawned.clan2, players: clan2Players, total: t2Total };
        // Winner on top — undecided (equal totals or zero scores) keeps clan1 on top.
        const [top, bottom] = t2Total > t1Total ? [board2, board1] : [board1, board2];
        const decided = t1Total !== t2Total && (t1Total > 0 || t2Total > 0);

        // Resolve the game mode for this game: prefer match.gameModes[g-1]
        // (the canonical mode order set when the match was forged), fall back
        // to the gameMode field on a slot in this bucket.
        const matchModes = (data.match as { gameModes?: string[] }).gameModes;
        const modeRaw = matchModes?.[g - 1] ?? data.slotsByGame?.[g]?.[0]?.gameMode;
        const modeLabel = gameModeLabel(modeRaw);
        return (
          <div key={g} className="mb-5 rounded-lg bg-paper-inset p-3">
            <div className="mb-2 flex items-baseline gap-3">
              <div className="font-display text-sm font-bold uppercase tracking-wider text-iron-100">Game {g}</div>
              {modeLabel && (
                <span className="rounded bg-white/[0.06] px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-ink-secondary">
                  {modeLabel}
                </span>
              )}
              <div className="font-mono text-[11px] text-iron-500">
                {decided ? `${top.name} ${top.total} – ${bottom.total} ${bottom.name}` : `${board1.name} ${t1Total} – ${t2Total} ${board2.name} (undecided)`}
              </div>
            </div>
            <TeamScoreboard
              team={top}
              isWinner={decided}
              cells={cells}
              fieldOrder={fieldOrder}
              extractedNames={extractedNames}
              onCorrect={(raw, suggested) => setCorrection({ raw, suggested })}
            />
            <TeamScoreboard
              team={bottom}
              isWinner={false}
              cells={cells}
              fieldOrder={fieldOrder}
              extractedNames={extractedNames}
              onCorrect={(raw, suggested) => setCorrection({ raw, suggested })}
            />
          </div>
        );
      })}

      <DropDiagnosticsPanel
        drops={allDrops}
        roster={roster}
        onCorrect={(raw, suggested) => setCorrection({ raw, suggested })}
      />

      {correction && (
        <CorrectionDialog
          raw={correction.raw}
          suggested={correction.suggested}
          roster={roster}
          compId={spawned.compId}
          onClose={() => setCorrection(null)}
          onSaved={() => {
            setCorrection(null);
            queryClient.invalidateQueries({ queryKey: ['knowledge', 'name-snap', spawned.compId] });
          }}
        />
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TeamScoreboard — one team's per-game board (4 player rows). Cells expose
// both the consensus value and the agreement confidence side-by-side so the
// lab user can see at a glance which numbers the pipeline is confident about.
// ─────────────────────────────────────────────────────────────────────────────

type GameCell = { player: string; field: string; consensus?: number; agreement: number; reads: number };

function ScoreCell({ cell }: { cell: GameCell | undefined }) {
  const v = cell?.consensus;
  const a = cell?.agreement ?? 0;
  const reads = cell?.reads ?? 0;
  const valueColour = v === undefined ? 'text-iron-700'
    : a === 1 ? 'text-emerald-300'
    : a >= 0.67 ? 'text-iron-100'
    : a >= 0.5 ? 'text-amber-300'
    : 'text-red-300';
  const confColour = a >= 0.67 ? 'text-iron-500'
    : a >= 0.5 ? 'text-amber-500'
    : 'text-red-400';
  return (
    <td
      className={cn('px-2 py-1.5 text-right tabular-nums', valueColour)}
      title={cell ? `${reads} read${reads === 1 ? '' : 's'}, ${(a * 100).toFixed(0)}% agreement` : 'no readings'}
    >
      <div className="leading-tight">{v ?? '—'}</div>
      {cell ? (
        <div className={cn('text-[9px] leading-tight', confColour)}>
          {Math.round(a * 100)}% · {reads}r
        </div>
      ) : (
        <div className="text-[9px] leading-tight text-iron-700">—</div>
      )}
    </td>
  );
}

function TeamScoreboard({
  team, isWinner, cells, fieldOrder, extractedNames, onCorrect,
}: {
  team: { name: string; players: string[]; total: number };
  isWinner: boolean;
  cells: GameCell[];
  fieldOrder: string[];
  extractedNames: Record<string, NameVote[]>;
  onCorrect: (raw: string, suggested: string | null) => void;
}) {
  // Click a player name to expand a detail panel showing every consensus
  // field for that player (including ones not surfaced as columns, like
  // K and D on HP, plus a computed K/D ratio). One open at a time per board.
  const [expanded, setExpanded] = useState<string | null>(null);
  return (
    <div className={cn(
      'mb-2 overflow-hidden rounded-md',
      isWinner ? 'bg-white/[0.05]' : 'bg-white/[0.02]',
    )}>
      <div className={cn(
        'flex items-baseline gap-2 px-3 py-1.5 font-mono text-[11px]',
        isWinner ? 'text-ink-primary' : 'text-iron-400',
      )}>
        {isWinner && <Trophy size={11} className="-mb-0.5 inline text-accent-500" />}
        <span className="font-display font-bold uppercase tracking-wider">{team.name}</span>
        <span className="ml-auto tabular-nums">{team.total}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-left font-mono text-xs">
          <thead className="text-iron-500">
            <tr>
              <th className="px-2 py-1 font-display uppercase tracking-wider">Player</th>
              {fieldOrder.map(f => <th key={f} className="px-2 py-1 text-right font-display uppercase tracking-wider">{FIELD_LABEL[f]}</th>)}
            </tr>
          </thead>
          <tbody>
            {team.players.map(p => {
              const namesForP = extractedNames[p] ?? [];
              const topName = namesForP[0]?.raw;
              const variantsCount = namesForP.length;
              const isOpen = expanded === p;
              // Defensive: `p` is a roster-snapped player key and should never
              // be null post-fix (see snapToRosterScored/normaliseName above),
              // but a null OCR name rendering as blank beats a future
              // regression white-screening the whole card.
              const pSafe = p ?? '';
              return (
                <Fragment key={p}>
                  <tr
                    className={cn(
                      'cursor-pointer border-t border-white/[0.04] hover:bg-white/[0.04]',
                      isOpen && 'bg-white/[0.04]',
                    )}
                    onClick={() => setExpanded(prev => (prev === p ? null : p))}
                  >
                    <td className="whitespace-nowrap px-2 py-1.5">
                      <div className="flex items-center gap-2">
                        <span className={cn('inline-block w-3 text-iron-500 transition-transform', isOpen && 'rotate-90 text-iron-300')}>▸</span>
                        <div className="min-w-0">
                          <div className="truncate text-iron-100" title={topName}>{topName ?? <span className="text-iron-500">—</span>}</div>
                          <div className="truncate font-mono text-[9px] text-iron-500" title={pSafe}>
                            {pSafe.length > 24 ? `${pSafe.slice(0, 8)}…${pSafe.slice(-8)}` : (pSafe || <span className="text-iron-500">—</span>)}
                            {variantsCount > 1 && <span className="ml-1 text-iron-500">· {variantsCount} variants</span>}
                          </div>
                        </div>
                        {topName && topName.toLowerCase() !== pSafe.toLowerCase() && (
                          <button
                            onClick={(e) => { e.stopPropagation(); onCorrect(topName, p); }}
                            className="ml-auto shrink-0 rounded p-0.5 text-iron-500 hover:bg-white/[0.06] hover:text-iron-200"
                            title={`Pin "${topName}" → "${p}"`}
                          >
                            <Pencil size={10} />
                          </button>
                        )}
                      </div>
                    </td>
                    {fieldOrder.map(f => <ScoreCell key={f} cell={cells.find(c => c.player === p && c.field === f)} />)}
                  </tr>
                  {isOpen && (
                    <tr className="bg-paper-inset">
                      <td colSpan={1 + fieldOrder.length} className="px-3 py-3">
                        <PlayerDetailPanel
                          player={p}
                          gamertag={topName}
                          variants={namesForP}
                          cells={cells.filter(c => c.player === p)}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {team.players.length === 0 && (
              <tr className="border-t border-white/[0.04]">
                <td colSpan={1 + fieldOrder.length} className="px-2 py-2 text-center text-[10px] italic text-iron-500">
                  no rows snapped to this team
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PlayerDetailPanel — expanded per-player breakdown showing every consensus
// field captured for that player (incl. those not in the column header), plus
// a derived K/D ratio. The cells array is already aggregated; this just lays
// out one player's slice of it as a labelled grid.
// ─────────────────────────────────────────────────────────────────────────────

const DETAIL_FIELDS: Array<{ field: string; label: string }> = [
  { field: 'score', label: 'Score' },
  { field: 'objScore', label: 'Obj Score' },
  { field: 'kills', label: 'Eliminations' },
  { field: 'deaths', label: 'Deaths' },
  { field: 'time', label: 'Time' },
  { field: 'plants', label: 'Plants' },
  { field: 'defuses', label: 'Defuses' },
  { field: 'overloads', label: 'Overloads' },
  { field: 'captures', label: 'Captures' },
];

function PlayerDetailPanel({
  player, gamertag, variants, cells,
}: {
  player: string;
  gamertag: string | undefined;
  variants: NameVote[];
  cells: GameCell[];
}) {
  const cellByField = new Map(cells.map(c => [c.field, c]));
  const k = cellByField.get('kills')?.consensus;
  const d = cellByField.get('deaths')?.consensus;
  const ratio =
    typeof k === 'number' && typeof d === 'number'
      ? (k / Math.max(d, 1)).toFixed(2)
      : null;

  return (
    <div>
      <div className="mb-3 flex items-baseline gap-2 font-mono text-[11px]">
        <span className="font-display text-sm font-bold uppercase tracking-wider text-iron-100">
          {gamertag ?? player}
        </span>
        <span className="text-iron-500">snap → {player}</span>
        {variants.length > 1 && (
          <span className="text-iron-500">· {variants.length} OCR variants</span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
        {DETAIL_FIELDS.map(({ field, label }) => {
          const cell = cellByField.get(field);
          return (
            <DetailStat
              key={field}
              label={label}
              cell={cell}
            />
          );
        })}
        <DetailStat label="K/D Ratio" derived={ratio ?? '—'} />
      </div>
      {variants.length > 1 && (
        <div className="mt-3 pt-2">
          <div className="mb-1 font-mono text-[10px] uppercase tracking-wider text-iron-500">Raw OCR variants</div>
          <div className="flex flex-wrap gap-1.5 font-mono text-[10px]">
            {variants.map(v => (
              <span key={v.raw} className="rounded bg-white/[0.05] px-1.5 py-0.5 text-iron-300">
                {v.raw} <span className="text-iron-500">×{v.count}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DetailStat({ label, cell, derived }: { label: string; cell?: GameCell; derived?: string }) {
  const v = derived ?? cell?.consensus ?? '—';
  const a = cell?.agreement ?? 0;
  const reads = cell?.reads ?? 0;
  const colour = derived ? 'text-iron-100'
    : v === '—' ? 'text-iron-700'
    : a === 1 ? 'text-emerald-300'
    : a >= 0.67 ? 'text-iron-100'
    : a >= 0.5 ? 'text-amber-300'
    : 'text-red-300';
  return (
    <div className="rounded bg-white/[0.04] px-2 py-1.5">
      <div className="font-mono text-[9px] uppercase tracking-wider text-iron-500">{label}</div>
      <div className={cn('font-display text-base font-bold leading-tight tabular-nums', colour)}>{v}</div>
      {!derived && cell ? (
        <div className="font-mono text-[9px] text-iron-500">{Math.round(a * 100)}% · {reads}r</div>
      ) : (
        <div className="font-mono text-[9px] text-iron-700">{derived ? 'derived' : '—'}</div>
      )}
    </div>
  );
}

function DropDiagnosticsPanel({ drops, roster, onCorrect }: {
  drops: DropDiagnostic[];
  roster: string[];
  onCorrect: (raw: string, suggested: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  if (drops.length === 0) return null;

  // Collapse identical raw OCR strings — same OCR mistake firing across many
  // shots is one rule waiting to be made.
  const dedupedMap = new Map<string, { raw: string; bestCandidate: string | null; score: number; count: number; games: Set<number> }>();
  for (const d of drops) {
    const e = dedupedMap.get(d.raw);
    if (e) { e.count++; e.games.add(d.gameNum); }
    else dedupedMap.set(d.raw, { raw: d.raw, bestCandidate: d.bestCandidate, score: d.score, count: 1, games: new Set([d.gameNum]) });
  }
  const deduped = [...dedupedMap.values()].sort((a, b) => b.count - a.count || b.score - a.score);

  return (
    <div className="mt-4 rounded-lg bg-white/[0.04] p-3">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex w-full items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-amber-300 hover:text-amber-200"
      >
        {open ? '▾' : '▸'} OCR drop diagnostics ({drops.length} dropped, {deduped.length} unique)
        <span className="text-iron-500 normal-case">— each row is a rule waiting to be made</span>
      </button>
      {open && (
        <div className="mt-3 overflow-x-auto">
          <table className="min-w-full text-left font-mono text-[11px]">
            <thead className="text-iron-500">
              <tr>
                <th className="px-2 py-1 font-display uppercase tracking-wider">OCR saw</th>
                <th className="px-2 py-1 font-display uppercase tracking-wider">closest roster</th>
                <th className="px-2 py-1 text-right font-display uppercase tracking-wider">score</th>
                <th className="px-2 py-1 text-right font-display uppercase tracking-wider">×</th>
                <th className="px-2 py-1 text-right font-display uppercase tracking-wider">games</th>
                <th className="px-2 py-1 font-display uppercase tracking-wider"></th>
              </tr>
            </thead>
            <tbody>
              {deduped.slice(0, 30).map(d => (
                <tr key={d.raw} className="border-t border-white/[0.04]">
                  <td className="whitespace-nowrap px-2 py-1 text-iron-200">{d.raw}</td>
                  <td className="whitespace-nowrap px-2 py-1 text-iron-400">
                    {d.bestCandidate ?? <span className="text-iron-500">none</span>}
                  </td>
                  <td className={cn(
                    'px-2 py-1 text-right tabular-nums',
                    d.score >= 0.45 ? 'text-amber-300'
                      : d.score >= 0.30 ? 'text-iron-300'
                      : 'text-iron-500',
                  )}>{d.score.toFixed(2)}</td>
                  <td className="px-2 py-1 text-right tabular-nums text-iron-300">{d.count}</td>
                  <td className="px-2 py-1 text-right tabular-nums text-iron-500">{[...d.games].sort().join(',')}</td>
                  <td className="px-2 py-1">
                    <button
                      onClick={() => onCorrect(d.raw, d.bestCandidate)}
                      className="rounded bg-white/[0.06] px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-iron-300 hover:bg-white/[0.1]"
                    >
                      teach
                    </button>
                  </td>
                </tr>
              ))}
              {deduped.length > 30 && (
                <tr className="border-t border-white/[0.04]">
                  <td colSpan={6} className="px-2 py-1 text-center text-iron-500">… and {deduped.length - 30} more</td>
                </tr>
              )}
            </tbody>
          </table>
          {roster.length === 0 && (
            <p className="mt-2 font-mono text-[10px] text-iron-500">no roster supplied — every row drops</p>
          )}
        </div>
      )}
    </div>
  );
}

function CorrectionDialog({ raw, suggested, roster, compId, onClose, onSaved }: {
  raw: string;
  suggested: string | null;
  roster: string[];
  compId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [target, setTarget] = useState<string>(suggested ?? '');
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => api.knowledgeAddNameSnap(compId, raw, target),
    onMutate: () => { setPending(true); setErr(null); },
    onSuccess: () => { setPending(false); onSaved(); },
    onError: (e: Error) => { setPending(false); setErr(parseApiError(e).message); },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-lg bg-paper-panel p-5 shadow-[0_12px_32px_-6px_rgba(0,0,0,0.6)]"
        onClick={e => e.stopPropagation()}
      >
        <h3 className="font-display text-sm font-bold uppercase tracking-wide text-iron-100">
          Teach the lab
        </h3>
        <p className="mt-1 font-mono text-[11px] text-iron-400">
          Pin this OCR'd name to a roster gamertag. Future shots will snap automatically.
        </p>

        <div className="mt-4 space-y-2">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-wider text-iron-500">OCR saw</label>
            <div className="mt-0.5 rounded bg-paper-inset px-2 py-1.5 font-mono text-xs text-iron-100">
              {raw}
            </div>
          </div>

          <div>
            <label className="font-mono text-[10px] uppercase tracking-wider text-iron-500">Snap to</label>
            <select
              value={target}
              onChange={e => setTarget(e.target.value)}
              className="input mt-0.5 w-full px-2 py-1.5 font-mono text-xs"
            >
              <option value="">— choose roster player —</option>
              {roster.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
        </div>

        {err && <p className="mt-3 font-mono text-[10px] text-red-300">{err}</p>}

        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className={cn('rounded bg-white/[0.06] px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-iron-300 hover:bg-white/[0.1]', MORPH)}
          >cancel</button>
          <button
            onClick={() => save.mutate()}
            disabled={!target || pending}
            className={cn('rounded bg-emerald-600 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-white hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50', MORPH)}
          >
            {pending ? 'saving…' : 'save rule'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ActivityFeedPanel — renders client lifecycle log + server-streamed pipeline events.
// Server events come from the OCRFEED# items written by OcrBatchRunner; they
// have a 1h DDB TTL so the table self-cleans.

function shortField(f: string): string {
  return ({ score: 'score', objScore: 'obj', kills: 'K', deaths: 'D', time: 't', plants: 'plants', overloads: 'OL' } as Record<string, string>)[f] ?? f;
}

// Exported for direct unit testing (same convention as normaliseName /
// snapToRosterScored / aggregateConsensus / inferClan above) — see
// MatchCardDemo.test.ts for the RED→GREEN proof of the crash this switch
// used to have (missing QUALITY_DISPUTED case, no default).
export function formatEvent(e: OcrProgressEvent): { line: string; tone: 'info' | 'good' | 'warn' | 'bad' } {
  switch (e.kind) {
    case 'BATCH_STARTED':       return { line: `► batch started — ${e.shotsTotal} shots`, tone: 'info' };
    case 'SHOT_ENHANCED':       return { line: `· ${e.shotId.slice(-12)}: enhanced ${e.sizeBeforeKb}KB→${e.sizeAfterKb}KB (${e.ms}ms)`, tone: 'info' };
    case 'SHOT_QUALITY':        return { line: `· ${e.shotId.slice(-12)}: quality=${e.quality.toFixed(2)}${e.flags?.length ? ` [${e.flags.join(',')}]` : ''}`, tone: e.quality < 0.4 ? 'warn' : 'info' };
    case 'SHOT_STAGE1_START':   return { line: `→ ${e.shotId.slice(-12)}: Stage 1 OCR…`, tone: 'info' };
    case 'SHOT_STAGE1_DONE':    return { line: `✓ ${e.shotId.slice(-12)}: parsed ${e.rowsParsed} rows · ${e.tokensIn}/${e.tokensOut}tk · ${e.ms}ms${e.mode ? ` · ${e.mode}` : ''}${e.mapName ? ` @ ${e.mapName}` : ''}`, tone: 'good' };
    case 'SHOT_STAGE1_ERROR':   return { line: `✗ ${e.shotId.slice(-12)}: Stage 1 ERROR — ${e.error}`, tone: 'bad' };
    case 'SHOT_NAMES_SNAPPED':  return { line: `~ ${e.shotId.slice(-12)}: ${e.snapsApplied} name${e.snapsApplied === 1 ? '' : 's'} snapped`, tone: e.snapsApplied > 0 ? 'info' : 'info' };
    // W-C: same rendering as OcrActivityFeedPanel.tsx's copy — kept in sync
    // by hand per this file's existing "Lab stays as-is except new event
    // kinds" precedent (see the W-A note below on BATCH_FAILED/SLOT_PERSIST_FAILED).
    // lab-crash-fix: that precedent was NOT followed when QUALITY_DISPUTED
    // was added (AC2, ocrBatchRunner/index.ts) — this switch had no case for
    // it and no default, so a live QUALITY_DISPUTED event mid-batch made
    // formatEvent implicitly return undefined and crashed the destructure at
    // this function's call site. The crash fix below restores the precedent
    // (new case added) and also adds a default branch so a future missed
    // kind degrades to a safe fallback row instead of white-screening the
    // whole Lab again. The byte-for-byte restraint above concerned
    // EVENT-SHAPE compatibility, not crash safety — this fix overrides it.
    case 'SHOT_ROSTER_CHECKED': {
      const parts: string[] = [];
      if (e.unexpectedNames.length > 0) parts.push(`${e.unexpectedNames.length} unexpected: ${e.unexpectedNames.join(', ')}`);
      if (e.missingRosterMembers.length > 0) parts.push(`${e.missingRosterMembers.length} missing: ${e.missingRosterMembers.join(', ')}`);
      return parts.length > 0
        ? { line: `⚠ ${e.shotId.slice(-12)}: roster check — ${parts.join(' · ')}`, tone: 'warn' }
        : { line: `· ${e.shotId.slice(-12)}: roster check clean`, tone: 'info' };
    }
    case 'CONSENSUS_GAME_DONE': return { line: `✓ G${e.gameNum} consensus: ${e.cellsAgreed} agreed, ${e.cellsDisputed} disputed`, tone: e.cellsDisputed > 0 ? 'warn' : 'good' };
    case 'STAGE2_QUEUED':       return { line: `→ Stage 2: ${e.cellsCount} disputed cell${e.cellsCount === 1 ? '' : 's'} queued for re-read`, tone: 'warn' };
    case 'STAGE2_CELL_RESOLVED':return { line: `✓ Stage 2 G${e.gameNum} ${e.playerKey}.${shortField(e.field)}: ${e.before} → ${e.after} (${e.ms}ms)`, tone: 'good' };
    case 'STAGE2_CELL_KEPT':    return { line: `· Stage 2 G${e.gameNum} ${e.playerKey}.${shortField(e.field)}: kept ${e.value}${e.reason ? ` (${e.reason})` : ''}`, tone: e.reason ? 'warn' : 'info' };
    case 'BATCH_COMPLETE':      return { line: `■ batch complete in ${(e.totalMs / 1000).toFixed(1)}s · S1=${e.stage1Calls} S2=${e.stage2Calls} · cost ~$${e.estimatedUsd.toFixed(4)}`, tone: 'good' };
    case 'MANUAL_ROUTED':       return { line: `⚠ routed to MANUAL entry — ${e.message}`, tone: 'warn' };
    // W-A: these two new kinds are additions this wave (formerly-silent
    // failure paths now emit a trace) — the Lab feed must render them too,
    // not just MatchCardLive's copy, or a founder watching the Lab sees
    // nothing when a batch fails outright.
    case 'BATCH_FAILED':        return { line: `✗ BATCH FAILED — ${e.message}`, tone: 'bad' };
    case 'SLOT_PERSIST_FAILED': return { line: `⚠ some results failed to save, card held for review — ${e.message}`, tone: 'bad' };
    // AC2: pipeline-flagged extraction quality issue — card held at DISPUTED
    // rather than auto-VERIFIED. See the lab-crash-fix note above: this kind
    // was missing from this switch entirely, which is the actual crash.
    case 'QUALITY_DISPUTED':    return { line: `⚠ ${e.message}`, tone: 'bad' };
    // Roster-integrity flag surfacing (Lisa product ruling, 2026-07-21) —
    // same kind + same muted rendering as OcrActivityFeedPanel.tsx's copy
    // (kept in sync per this file's existing precedent — see the W-C note
    // above). OBSERVE-ONLY visibility, not a dispute/error signal in its own
    // right (the card's own DISPUTED/VERIFIED outcome is untouched by this
    // flag) — 'info' tone, no colour-coded alarm strip, this is exactly the
    // "an admin adjudicating a DISPUTED card in the lab never saw that a
    // submitter had left the clan" gap this event exists to close.
    case 'SUBMITTER_NOT_ROSTERED': return { line: e.message, tone: 'info' };
    default: {
      // Defence in depth — never let an event kind this Lab doesn't
      // recognise yet (or a malformed feed row) take the whole panel down.
      const kind = (e as { kind?: unknown } | undefined)?.kind;
      return { line: `· unrecognised event${typeof kind === 'string' ? ` (${kind})` : ''}`, tone: 'info' };
    }
  }
}

function ActivityFeedPanel({ events, batchInFlight, clientLog }: { events: OcrFeedEvent[]; batchInFlight: boolean; clientLog: string[] }) {
  const ref = useRef<HTMLPreElement>(null);
  useEffect(() => { if (ref.current) ref.current.scrollTop = ref.current.scrollHeight; }, [events.length, clientLog.length]);

  if (events.length === 0 && clientLog.length === 0) return null;

  return (
    <section className="rounded-xl bg-paper-elevated p-4">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="font-display text-xs font-bold uppercase tracking-wider text-iron-400">Live activity feed</h2>
        <span className="font-mono text-[10px] text-iron-500">
          {batchInFlight ? <span className="text-emerald-400">● streaming</span> : 'idle'} · {events.length} server events · auto-cleans after 1h
        </span>
      </div>
      <pre ref={ref} className="max-h-72 overflow-y-auto whitespace-pre-wrap font-mono text-[11px] leading-relaxed text-iron-300">
        {clientLog.map((l, i) => <div key={`c${i}`} className="text-iron-400">{l}</div>)}
        {events.map(e => {
          const { line, tone } = formatEvent(e.event);
          const ts = new Date(e.ts).toISOString().slice(11, 23);
          const toneCls = tone === 'good' ? 'text-emerald-300' : tone === 'warn' ? 'text-amber-300' : tone === 'bad' ? 'text-red-300' : 'text-iron-200';
          return <div key={e.sk} className={toneCls}><span className="text-iron-500">{ts}</span>  {line}</div>;
        })}
      </pre>
    </section>
  );
}

export default MatchCardDemo;
